import React, { useRef, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Moveable from 'react-moveable'

import { IMediaItem } from 'types/media'
import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import editorSlice from 'store/features/editor/slice'

import { editorSelectors } from 'store/features/editor/selectors'
import CanvasTextEditor from './canvas-text-editor'

import * as Styled from './styled'
import { useMovableActions } from '../MovableItemsLayer/hooks/use-movable-actions'
import { useLiveSizes } from '../MovableItemsLayer/hooks/use-live-sizes'
import {
  useDirections,
  MIN_RESIZE_SIZE
} from '../MovableItemsLayer/hooks/use-directions'
import { useSnapGuidelines } from '../MovableItemsLayer/hooks/use-snap-guidelines'
import { MAX_FONT_SIZE } from 'consts/text'
import { CANVAS_ITEM_FRAME_CLASS_NAME, PROJECT_MEDIA_TYPES } from 'consts'

import {
  buildFrameElementToIdMap,
  CLICK_MOVE_THRESHOLD_SQ,
  findTopmostCanvasItemIdAtPoint,
  frameDisplayAreaSq
} from './utils'

interface ICanvasItemFrameProps {
  item: IMediaItem
  mediaItemsById: Map<string, IMediaItem>
  isVisible: boolean
  canvasScale: number
  isGenerating: boolean
  isActive: boolean
  isMovableVisible: boolean
  /** Moveable handles + active outline; hidden while timeline plays (matches media toolbar). */
  isSelectionChromeVisible: boolean
  targetRefsMap: React.MutableRefObject<Map<string, HTMLDivElement>>
  canvasWidth: number
  canvasHeight: number
  onOpenZOrderMenu: (args: { itemId: string; x: number; y: number }) => void
  activeTimelineItemId: string | null
  activeMoveableRef: React.MutableRefObject<Moveable | null>
  /** Pauses timeline if currently playing. Called before any canvas-driven selection. */
  pauseIfPlaying: () => void
}

const CanvasItemFrame: React.FC<ICanvasItemFrameProps> = ({
  item,
  mediaItemsById,
  canvasScale,
  isActive,
  isMovableVisible,
  isSelectionChromeVisible,
  targetRefsMap,
  canvasWidth,
  canvasHeight,
  onOpenZOrderMenu,
  activeTimelineItemId,
  activeMoveableRef,
  pauseIfPlaying
}) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const moveableRef = useRef<Moveable>(null)
  const isText = item.type === PROJECT_MEDIA_TYPES.text
  const editingTextId = useSelector(editorSelectors.editingTextId)
  const isEditing = isText && editingTextId === item.id
  // Text side-handle drag (E/W) changes wrap width without scaling fontSize,
  // so keepRatio must be off for that gesture. Corners keep the existing
  // proportional-scale behavior.
  const [resizeKeepRatio, setResizeKeepRatio] = useState(true)
  const selectGestureRef = useRef({
    exceeded: false,
    startX: 0,
    startY: 0
  })
  const selectGestureCleanupRef = useRef<(() => void) | null>(null)
  const routedDragToActiveRef = useRef(false)
  /** True if pointerdown hit stack included the currently selected item's frame. */
  const pointerDownHitIncludesActiveFrameRef = useRef(false)

  const assignMoveableRef = useCallback(
    (instance: Moveable | null) => {
      moveableRef.current = instance
      if (!instance) {
        activeMoveableRef.current = null
        return
      }
      if (isSelectionChromeVisible) {
        activeMoveableRef.current = instance
      }
    },
    [activeMoveableRef, isSelectionChromeVisible]
  )

  const { liveSize, liveCoordinates, isResizingRef, isTextSideResizingRef } =
    useLiveSizes(item, moveableRef)
  const displayWidth = liveSize?.width ?? item.width
  const displayHeight = liveSize?.height ?? item.height
  const displayLeft = liveCoordinates?.x ?? item.coordinates.x
  const displayTop = liveCoordinates?.y ?? item.coordinates.y

  const { getRenderDirections, directionVectorToKey, setActiveResizeDir } =
    useDirections(displayHeight, isActive)

  const snapProps = useSnapGuidelines({
    itemId: item.id,
    targetRef: frameRef,
    targetRefsMap,
    canvasWidth,
    canvasHeight,
    enabled: isSelectionChromeVisible
  })

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!isActive) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      onOpenZOrderMenu({ itemId: item.id, x: e.clientX, y: e.clientY })
    },
    [isActive, item.id, onOpenZOrderMenu]
  )

  const markSelectGestureExceeded = useCallback(() => {
    selectGestureRef.current.exceeded = true
  }, [])

  const startSelectGestureTracking = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) {
        return
      }
      pauseIfPlaying()
      selectGestureCleanupRef.current?.()
      routedDragToActiveRef.current = false

      const hitStack = document.elementsFromPoint(e.clientX, e.clientY)
      const activeFrameEl = activeTimelineItemId
        ? (targetRefsMap.current.get(activeTimelineItemId) ?? null)
        : null
      pointerDownHitIncludesActiveFrameRef.current = !!(
        activeFrameEl && hitStack.includes(activeFrameEl)
      )

      const frameElementToId = buildFrameElementToIdMap(targetRefsMap.current)

      selectGestureRef.current = {
        exceeded: false,
        startX: e.clientX,
        startY: e.clientY
      }

      const selectItemAndScheduleDrag = (
        targetItem: IMediaItem,
        pointerEvent: PointerEvent
      ) => {
        routedDragToActiveRef.current = true
        pauseIfPlaying()
        dispatch(
          editorThunks.selectSingleTimelineItem({
            id: targetItem.id,
            type: targetItem.type
          })
        )
        const eventForDrag = pointerEvent
        requestAnimationFrame(() => {
          const movable = activeMoveableRef.current
          const dragTarget = targetRefsMap.current.get(targetItem.id)
          if (movable && dragTarget) {
            movable.dragStart(eventForDrag, dragTarget)
          }
        })
      }

      const startDraggingCurrentSelection = (
        moveable: Moveable,
        activeElement: HTMLElement,
        pointerEvent: PointerEvent
      ) => {
        routedDragToActiveRef.current = true
        moveable.dragStart(pointerEvent, activeElement)
      }

      const onMove = (mouseEvent: PointerEvent) => {
        const g = selectGestureRef.current
        const dx = mouseEvent.clientX - g.startX
        const dy = mouseEvent.clientY - g.startY
        const wasExceeded = g.exceeded
        if (dx * dx + dy * dy > CLICK_MOVE_THRESHOLD_SQ) {
          g.exceeded = true
        }

        if (
          wasExceeded ||
          !g.exceeded ||
          isActive ||
          routedDragToActiveRef.current
        ) {
          return
        }

        const moveable = activeMoveableRef.current
        const activeElement = activeTimelineItemId
          ? targetRefsMap.current.get(activeTimelineItemId)
          : null

        const pressedOnActiveFrame =
          pointerDownHitIncludesActiveFrameRef.current &&
          activeTimelineItemId !== null &&
          activeTimelineItemId !== item.id

        if (pressedOnActiveFrame) {
          const topId = findTopmostCanvasItemIdAtPoint(
            mouseEvent.clientX,
            mouseEvent.clientY,
            frameElementToId
          )
          const topMedia = topId ? mediaItemsById.get(topId) : undefined
          const selectedMedia = mediaItemsById.get(activeTimelineItemId)

          const topLayerIsSmallerOverlay =
            topMedia !== undefined &&
            selectedMedia !== undefined &&
            frameDisplayAreaSq(topMedia) < frameDisplayAreaSq(selectedMedia)

          if (topLayerIsSmallerOverlay) {
            selectItemAndScheduleDrag(topMedia, mouseEvent)
          } else if (moveable && activeElement) {
            startDraggingCurrentSelection(moveable, activeElement, mouseEvent)
          }
          return
        }

        const pressedOutsideActiveFrame =
          !pointerDownHitIncludesActiveFrameRef.current &&
          item.id !== activeTimelineItemId

        if (pressedOutsideActiveFrame) {
          selectItemAndScheduleDrag(item, mouseEvent)
        }
      }

      const onEnd = () => {
        routedDragToActiveRef.current = false
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onEnd)
        window.removeEventListener('pointercancel', onEnd)
        selectGestureCleanupRef.current = null
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onEnd)
      window.addEventListener('pointercancel', onEnd)
      selectGestureCleanupRef.current = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onEnd)
        window.removeEventListener('pointercancel', onEnd)
      }
    },
    [
      activeMoveableRef,
      activeTimelineItemId,
      isActive,
      item,
      mediaItemsById,
      targetRefsMap,
      pauseIfPlaying
    ]
  )

  const maybeHandleClick = useCallback(() => {
    // Inactive items: React's native onClick on the frame fires even for
    // small drags, so we keep the movement-threshold check (`exceeded` is
    // set by our own onMove handler in startSelectGestureTracking).
    // Active items: the Moveable's onClick callback already suppresses
    // drags internally (`e.isDrag` short-circuit in moveable.cjs.js
    // dragEnd), so we don't need our own threshold there — and we can't,
    // because Moveable's onDragStart pre-flips `exceeded` to true on press.
    if (!isActive && selectGestureRef.current.exceeded) {
      return
    }
    pauseIfPlaying()
    if (isActive) {
      // Second click on already-selected text enters edit mode with the
      // caret placed at the clicked glyph. Other types: no-op (already
      // selected — nothing to do on a repeat click).
      if (isText && !isEditing) {
        // Use the pointerdown coords captured by startSelectGestureTracking.
        // On mobile, Moveable's onClick passes a TouchEvent as `inputEvent`,
        // whose `clientX/clientY` are `undefined` (those live on
        // `changedTouches[0]`), so the click coords from there would be
        // NaN. The React PointerEvent we captured at pointerdown has
        // reliable clientX/clientY on both touch and mouse.
        const g = selectGestureRef.current
        dispatch(
          editorSlice.actions.setEditingTextEntryPoint({
            clientX: g.startX,
            clientY: g.startY
          })
        )
        dispatch(editorSlice.actions.setEditingTextId(item.id))
      }
      return
    }
    dispatch(
      editorThunks.selectSingleTimelineItem({
        id: item.id,
        type: item.type
      })
    )
  }, [item.id, item.type, isActive, isText, isEditing, pauseIfPlaying])

  // Manual double-tap detection. Browser `dblclick` is unreliable on touch:
  // iOS WebKit (Safari + Chrome) and Chrome DevTools mobile emulation often
  // swallow it. Detecting from pointerup ourselves works for mouse and touch
  // on every platform. Distance between the two taps (30px) already filters
  // real drags, so we don't reject on per-tap finger-drift.
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null)
  const handleDoubleTap = useCallback(
    (e: React.PointerEvent) => {
      if (!isText || isEditing) return
      const now = performance.now()
      const last = lastTapRef.current
      if (
        last &&
        now - last.time < 350 &&
        Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30
      ) {
        e.preventDefault()
        e.stopPropagation()
        lastTapRef.current = null
        dispatch(
          editorSlice.actions.setEditingTextEntryPoint({
            clientX: e.clientX,
            clientY: e.clientY
          })
        )
        dispatch(editorSlice.actions.setEditingTextId(item.id))
        return
      }
      lastTapRef.current = { time: now, x: e.clientX, y: e.clientY }
    },
    [isText, isEditing, item.id]
  )

  const {
    handleDrag,
    handleDragEnd,
    handleResizeStart: onResizeStart,
    handleResize,
    handleResizeEnd: onResizeEnd
  } = useMovableActions(item)

  const handleResizeStart = useCallback(
    (e: Parameters<typeof onResizeStart>[0]) => {
      markSelectGestureExceeded()
      isResizingRef.current = true
      const isCornerDirection = e.direction[0] !== 0 && e.direction[1] !== 0
      if (isCornerDirection) {
        setActiveResizeDir(directionVectorToKey(e.direction))
      }
      e.setMin([MIN_RESIZE_SIZE, MIN_RESIZE_SIZE])

      const isSideHandle = e.direction[1] === 0 && e.direction[0] !== 0
      if (isText) {
        setResizeKeepRatio(!isSideHandle)
        isTextSideResizingRef.current = isSideHandle
      }

      // Corner-drag of text scales fontSize proportionally; cap at MAX_FONT_SIZE.
      // Side-drag only changes wrap width and never touches fontSize, so the
      // fontSize-derived max doesn't apply.
      if (!isSideHandle && item.meta?.textData?.fontSize) {
        const maxScaling = MAX_FONT_SIZE / item.meta.textData.fontSize
        e.setMax([item.width * maxScaling, item.height * maxScaling])
      }
      onResizeStart(e)
    },
    [
      markSelectGestureExceeded,
      onResizeStart,
      setActiveResizeDir,
      directionVectorToKey,
      isResizingRef,
      isTextSideResizingRef,
      isText,
      item.width,
      item.height,
      item.meta?.textData?.fontSize
    ]
  )

  const handleResizeEnd = useCallback(
    (e: Parameters<typeof onResizeEnd>[0]) => {
      isResizingRef.current = false
      isTextSideResizingRef.current = false
      setActiveResizeDir(null)
      setResizeKeepRatio(true)
      onResizeEnd(e)
    },
    [onResizeEnd, isResizingRef, isTextSideResizingRef, setActiveResizeDir]
  )

  useEffect(() => {
    moveableRef.current?.updateRect()
  })

  useEffect(
    () => () => {
      selectGestureCleanupRef.current?.()
    },
    []
  )

  return (
    <>
      <Styled.ItemFrame
        ref={frameRef}
        className={CANVAS_ITEM_FRAME_CLASS_NAME}
        $left={displayLeft}
        $top={displayTop}
        $width={displayWidth}
        $height={displayHeight}
        $zIndex={item.zIndex}
        $rotation={item.rotation}
        $flipX={item.flip?.x ?? false}
        $isActive={isActive}
        $isMovableVisible={isMovableVisible}
        $isSelectionChromeVisible={isSelectionChromeVisible}
        onPointerDown={isEditing ? undefined : startSelectGestureTracking}
        onPointerUp={isEditing ? undefined : handleDoubleTap}
        onClick={isEditing ? undefined : maybeHandleClick}
        onContextMenu={handleContextMenu}
      >
        {isEditing && item.meta.textData ? (
          <CanvasTextEditor
            itemId={item.id}
            assetId={item.assetId}
            textData={item.meta.textData}
            width={displayWidth}
            height={displayHeight}
            canvasScale={canvasScale}
            rotation={item.rotation}
            flipX={item.flip?.x ?? false}
          />
        ) : null}
      </Styled.ItemFrame>

      {isSelectionChromeVisible && (
        <Moveable
          ref={assignMoveableRef}
          target={frameRef}
          zoom={canvasScale > 0 ? 1 / canvasScale : 1}
          origin={false}
          hideDefaultLines
          renderDirections={(() => {
            const base = getRenderDirections()
            if (!isText) return base
            // Large text: full corners + both side (wrap) handles.
            if (base.length > 1) return [...base, 'e', 'w']
            // Small text: single corner (bottom-left) + right side handle so
            // the user can still adjust wrap width without an oversized chrome.
            return [...base, 'e']
          })()}
          draggable
          onDragStart={markSelectGestureExceeded}
          onDrag={handleDrag}
          throttleDrag={0}
          onDragEnd={handleDragEnd}
          keepRatio={isText ? resizeKeepRatio : true}
          resizable
          throttleResize={0}
          onResizeStart={handleResizeStart}
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
          {...snapProps}
          onClick={({ isTarget }) => {
            if (isTarget) maybeHandleClick()
          }}
        />
      )}
    </>
  )
}

export default CanvasItemFrame
